import { useState, type DragEvent, type JSX, type ReactNode } from 'react';
import type { ListEntry, PropertyDescriptor } from '../../../core/definitions';
import { Icon } from '../../components/Icon';
import { useStudio } from '../../context';
import { safely } from '../../safe';
import { newEntryId } from './coerce';
import { AssetName, ImportStatus, useAssetImport } from './fields';

type ListDescriptor = Extract<PropertyDescriptor, { type: 'list' }>;

const ENTRY_DRAG_TYPE = 'application/x-studio-list-entry';

/** A problem with a URL entry, or null when it can be loaded. */
export function urlEntryProblem(text: string): string | null {
  try {
    const url = new URL(text.trim());
    return ['http:', 'https:', 'blob:', 'data:'].includes(url.protocol) ? null : 'Use an http:// or https:// address.';
  } catch {
    return 'Not a valid address.';
  }
}

/** Moves the entry at `from` so it ends up just before (or after) the entry currently at `over`. */
export function moveEntry<T>(list: readonly T[], from: number, over: number, after: boolean): T[] {
  if (from < 0 || from >= list.length || over < 0 || over >= list.length) return [...list];
  const out = [...list];
  const [moved] = out.splice(from, 1);
  let to = after ? over + 1 : over;
  if (from < to) to -= 1;
  out.splice(to, 0, moved!);
  return out;
}

/** Inline editor for one text or URL value; applies on Enter or when left, and refuses invalid URLs. */
function EntryText(props: { kind: 'text' | 'url'; value: string; label: string; onCommit(value: string): void }): JSX.Element {
  const { kind, value, label, onCommit } = props;
  const [draft, setDraft] = useState<string | null>(null);
  const text = draft ?? value;
  const problem = kind === 'url' && text.trim() !== '' ? urlEntryProblem(text) : null;
  const finish = (): void => {
    if (draft === null) return;
    if (problem === null && draft !== value) onCommit(kind === 'url' ? draft.trim() : draft);
    if (problem === null) setDraft(null);
  };
  return (
    <span className="ui-grow ui-col" style={{ gap: 2 }}>
      <input
        className={problem ? 'ui-input invalid' : 'ui-input'}
        type={kind === 'url' ? 'url' : 'text'}
        aria-label={label}
        aria-invalid={problem !== null || undefined}
        value={text}
        spellCheck={kind === 'text' ? undefined : false}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={finish}
        onKeyDown={(e) => {
          if (e.key === 'Enter') finish();
        }}
      />
      {problem ? <span className="ui-error-text">{problem}</span> : null}
    </span>
  );
}

export interface ListFieldProps {
  desc: ListDescriptor;
  value: ListEntry[];
  id: string;
  onChange(value: ListEntry[]): void;
  onCommit(): void;
  /** Renders the per-entry `fields` bound to one entry. */
  renderFields(entry: ListEntry, onEntryChange: (key: string, value: unknown) => void): ReactNode;
}

/**
 * An editable list of text, URL or asset entries. Every entry keeps a stable
 * id, so rows are keyed reliably and Remove takes out exactly the row it is
 * on even when two entries hold the same value. Reorder by dragging a row or
 * with the arrow buttons.
 */
export function ListField({ desc, value, id, onChange, onCommit, renderFields }: ListFieldProps): JSX.Element {
  const studio = useStudio();
  const importer = useAssetImport(desc.accept ?? '*/*', true);
  const [adding, setAdding] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [drop, setDrop] = useState<{ index: number; after: boolean } | null>(null);
  const [open, setOpen] = useState<Record<string, true>>({});
  const itemLabel = desc.itemLabel ?? (desc.item === 'asset' ? 'File' : desc.item === 'url' ? 'Address' : 'Entry');
  const reorderable = desc.reorderable !== false;
  const allowDuplicates = desc.allowDuplicates !== false;

  const save = (next: ListEntry[]): void => {
    onChange(next);
    onCommit();
  };

  /** New entries for `values`, minus any that would repeat an existing value when duplicates are not allowed. */
  const append = (values: readonly string[]): void => {
    const present = new Set(value.map((entry) => entry.value));
    const fresh: ListEntry[] = [];
    const repeated: string[] = [];
    for (const v of values) {
      if (!allowDuplicates && present.has(v)) {
        repeated.push(v);
        continue;
      }
      present.add(v);
      fresh.push({ id: newEntryId(), value: v });
    }
    setMessage(repeated.length > 0 ? `${repeated.length === 1 ? 'That entry is' : `${repeated.length} entries are`} already in the list.` : null);
    if (fresh.length > 0) save([...value, ...fresh]);
  };

  const update = (index: number, patch: Partial<ListEntry>): void => {
    const entry = value[index];
    if (!entry) return;
    if (patch.value !== undefined && !allowDuplicates && value.some((other, i) => i !== index && other.value === patch.value)) {
      setMessage('That entry is already in the list.');
      return;
    }
    setMessage(null);
    save(value.map((e, i) => (i === index ? { ...e, ...patch, id: e.id } : e)));
  };

  const remove = (index: number): void => save(value.filter((_, i) => i !== index));
  const duplicate = (index: number): void => {
    const entry = value[index];
    if (!entry) return;
    if (!allowDuplicates) {
      setMessage('This list does not allow the same entry twice.');
      return;
    }
    save([...value.slice(0, index + 1), { ...structuredClone(entry), id: newEntryId() }, ...value.slice(index + 1)]);
  };
  const move = (from: number, over: number, after: boolean): void => {
    const next = moveEntry(value, from, over, after);
    if (next.some((entry, i) => entry !== value[i])) save(next);
  };

  const addFiles = (): void =>
    void safely(studio, async () => {
      const ids = await importer.browse();
      if (ids.length > 0) append(ids);
    }, 'Import failed');

  const positionOf = (event: DragEvent<HTMLLIElement>): boolean => {
    const rect = event.currentTarget.getBoundingClientRect();
    return event.clientY - rect.top > rect.height / 2;
  };

  return (
    <div className="ui-col property-list" id={id} style={{ gap: 6 }}>
      {value.length === 0 ? <span className="ui-dim">No {itemLabel.toLowerCase()}s yet.</span> : null}
      <ol className="property-list-rows" aria-label={desc.label}>
        {value.map((entry, index) => (
          <li
            key={entry.id}
            data-entry={entry.id}
            className={['property-list-row', drop?.index === index ? (drop.after ? 'drop-after' : 'drop-before') : ''].join(' ')}
            draggable={reorderable}
            onDragStart={(e) => {
              if (!(e.target instanceof HTMLLIElement)) return;
              e.dataTransfer.setData(ENTRY_DRAG_TYPE, String(index));
              e.dataTransfer.effectAllowed = 'move';
            }}
            onDragOver={(e) => {
              if (!reorderable || !Array.from(e.dataTransfer.types).includes(ENTRY_DRAG_TYPE)) return;
              e.preventDefault();
              setDrop({ index, after: positionOf(e) });
            }}
            onDragLeave={() => setDrop(null)}
            onDrop={(e) => {
              e.preventDefault();
              setDrop(null);
              const from = Number(e.dataTransfer.getData(ENTRY_DRAG_TYPE));
              if (Number.isInteger(from)) move(from, index, positionOf(e));
            }}
          >
            <div className="ui-row">
              {reorderable ? (
                <span className="property-list-grip" aria-hidden="true" title="Drag to reorder">
                  ⋮⋮
                </span>
              ) : null}
              {desc.item === 'asset' ? (
                <AssetName assetId={entry.value} />
              ) : (
                <EntryText kind={desc.item} value={entry.value} label={`${itemLabel} ${index + 1}`} onCommit={(v) => update(index, { value: v })} />
              )}
              {desc.fields && desc.fields.length > 0 ? (
                <button
                  type="button"
                  className="ui-icon-btn"
                  aria-expanded={open[entry.id] === true}
                  aria-label={`${open[entry.id] ? 'Hide' : 'Show'} settings of ${itemLabel.toLowerCase()} ${index + 1}`}
                  title="Settings"
                  onClick={() =>
                    setOpen((o) => {
                      const next = { ...o };
                      if (next[entry.id]) delete next[entry.id];
                      else next[entry.id] = true;
                      return next;
                    })
                  }
                >
                  <Icon name="gear" />
                </button>
              ) : null}
              {reorderable ? (
                <>
                  <button type="button" className="ui-icon-btn" aria-label={`Move ${itemLabel.toLowerCase()} ${index + 1} up`} title="Move up" disabled={index === 0} onClick={() => move(index, index - 1, false)}>
                    <Icon name="up" />
                  </button>
                  <button
                    type="button"
                    className="ui-icon-btn"
                    aria-label={`Move ${itemLabel.toLowerCase()} ${index + 1} down`}
                    title="Move down"
                    disabled={index === value.length - 1}
                    onClick={() => move(index, index + 1, true)}
                  >
                    <Icon name="down" />
                  </button>
                </>
              ) : null}
              <button type="button" className="ui-icon-btn" aria-label={`Duplicate ${itemLabel.toLowerCase()} ${index + 1}`} title="Duplicate" onClick={() => duplicate(index)}>
                <Icon name="copy" />
              </button>
              <button type="button" className="ui-icon-btn" aria-label={`Remove ${itemLabel.toLowerCase()} ${index + 1}`} title="Remove" onClick={() => remove(index)}>
                <Icon name="trash" />
              </button>
            </div>
            {open[entry.id] && desc.fields ? (
              <div className="property-list-fields">{renderFields(entry, (key, v) => update(index, { [key]: v }))}</div>
            ) : null}
          </li>
        ))}
      </ol>
      {desc.item === 'asset' ? (
        <div>
          <button type="button" className="ui-btn" disabled={importer.busy} aria-busy={importer.busy} onClick={addFiles}>
            <Icon name="plus" /> {importer.busy ? 'Importing…' : `Add ${itemLabel.toLowerCase()}s…`}
          </button>
        </div>
      ) : null}
      {/* Asset lists may also point at an address instead of an imported file. */}
      <form
        className="ui-row"
        onSubmit={(e) => {
          e.preventDefault();
          const addressOnly = desc.item !== 'text';
          const text = addressOnly ? adding.trim() : adding;
          if (text.trim() === '' || (addressOnly && urlEntryProblem(text) !== null)) return;
          append([text]);
          setAdding('');
        }}
      >
        <input
          className="ui-input ui-grow"
          type={desc.item === 'text' ? 'text' : 'url'}
          aria-label={desc.item === 'asset' ? 'New address' : `New ${itemLabel.toLowerCase()}`}
          placeholder={desc.item === 'text' ? `New ${itemLabel.toLowerCase()}` : desc.item === 'asset' ? 'or add an address: https://…' : 'https://…'}
          value={adding}
          onChange={(e) => setAdding(e.target.value)}
        />
        <button type="submit" className="ui-btn" disabled={adding.trim() === '' || (desc.item !== 'text' && urlEntryProblem(adding) !== null)}>
          <Icon name="plus" /> Add
        </button>
      </form>
      <ImportStatus state={importer} />
      {message ? (
        <span className="ui-warn-text" role="status">
          {message}
        </span>
      ) : null}
    </div>
  );
}
